/**
 * Package Summary Card
 * Displays GC/SC amounts and price in a visually appealing card
 * Used in payment checkout flows
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { GCBadgeSVG, SCBadgeSVG } from '@/components/CurrencyBadge';
import { Sparkles } from 'lucide-react';

interface PackageSummaryCardProps {
  /** Total GC amount (base + bonus) */
  totalGC: number;
  /** Total SC amount (base + bonus) */
  totalSC: number;
  /** Price in dollars */
  price: number;
  /** Optional promo code applied */
  promoCode?: string;
  /** Promo reward type */
  promoRewardType?: string;
  /** Promo reward value (percentage) */
  promoRewardValue?: number;
  /** Additional class names */
  className?: string;
}

// Format large numbers with commas
const formatCoins = (value: number) => value.toLocaleString();

// Get accent color based on GC tier
const getAccentColor = (gcAmount: number) => {
  if (gcAmount >= 1000000) return 'from-purple-500 via-pink-500 to-purple-500';
  if (gcAmount >= 500000) return 'from-amber-500 via-orange-400 to-amber-500';
  if (gcAmount >= 250000) return 'from-yellow-400 via-amber-400 to-yellow-400';
  if (gcAmount >= 100000) return 'from-blue-400 via-cyan-400 to-blue-400';
  if (gcAmount >= 50000) return 'from-green-400 via-emerald-400 to-green-400';
  return 'from-slate-400 via-slate-300 to-slate-400';
};

// Get glow color based on GC tier
const getGlowColor = (gcAmount: number) => {
  if (gcAmount >= 1000000) return 'shadow-purple-500/20';
  if (gcAmount >= 500000) return 'shadow-amber-500/20';
  if (gcAmount >= 250000) return 'shadow-yellow-500/20';
  if (gcAmount >= 100000) return 'shadow-blue-500/20';
  if (gcAmount >= 50000) return 'shadow-green-500/20';
  return 'shadow-slate-500/10';
};

export const PackageSummaryCard: React.FC<PackageSummaryCardProps> = ({
  totalGC,
  totalSC,
  price,
  promoCode,
  promoRewardType,
  promoRewardValue,
  className,
}) => {
  const accentColor = getAccentColor(totalGC);
  const glowColor = getGlowColor(totalGC);

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl',
        'bg-card',
        'border border-border',
        `shadow-lg ${glowColor}`,
        className
      )}
    >
      {/* Animated accent line at top */}
      <div
        className={cn(
          'absolute top-0 left-0 right-0 h-1',
          `bg-gradient-to-r ${accentColor}`
        )}
      />

      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-radial from-white to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative p-5">
        {/* Currency display */}
        <div className="flex items-center justify-center gap-4 mb-4">
          {/* GC Amount */}
          {totalGC > 0 && (
            <div className="flex items-center gap-2">
              <GCBadgeSVG size={26} />
              <span className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-400">
                {formatCoins(totalGC)}
              </span>
            </div>
          )}

          {/* Plus separator */}
          {totalGC > 0 && totalSC > 0 && (
            <span className="text-muted-foreground font-bold">+</span>
          )}

          {/* SC Amount */}
          {totalSC > 0 && (
            <div className="flex items-center gap-2">
              <SCBadgeSVG size={26} />
              <div className="flex items-center gap-1.5">
                <span className="text-lg font-black text-transparent bg-clip-text bg-gradient-to-r from-green-300 to-emerald-400">
                  {formatCoins(totalSC)}
                </span>
                <span className="text-[10px] font-bold text-green-500/80 uppercase tracking-wider bg-green-500/10 px-1.5 py-0.5 rounded">
                  Free
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Price badge */}
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-muted border border-border">
            <Sparkles className="h-4 w-4 text-amber-400" />
            <span className="text-2xl font-black text-foreground">
              ${price.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Promo code section */}
        {promoCode && (
          <div className="mt-4 pt-3 border-t border-border">
            <div className="flex items-center justify-center gap-2 text-xs">
              <span className="text-muted-foreground">
                Promo: <span className="font-bold text-amber-400">{promoCode}</span>
              </span>
              {promoRewardType === 'discount' && promoRewardValue && (
                <span className="text-green-400 font-bold px-2 py-0.5 bg-green-500/10 rounded">
                  {promoRewardValue}% OFF
                </span>
              )}
              {promoRewardType === 'bonus' && promoRewardValue && (
                <span className="text-green-400 font-bold px-2 py-0.5 bg-green-500/10 rounded">
                  +{promoRewardValue}% Bonus
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
